import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Radar, Settings2, X, Check, Users, Loader2, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RadarPromptCard({ headers }) {
  const [clusters, setClusters] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [acceptCluster, setAcceptCluster] = useState(null);
  const [acceptNotes, setAcceptNotes] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadClusters = async () => {
    try {
      const r = await axios.get(`${API}/cop/radar/clusters/mine`, { headers });
      setClusters(r.data.clusters || []);
    } catch { /* silent — feature may not be enabled for this user */ }
  };

  const loadSettings = async () => {
    try {
      const r = await axios.get(`${API}/cop/radar/settings`, { headers });
      setSettings(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadClusters(); loadSettings(); /* eslint-disable-next-line */ }, []);

  const saveSettings = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cop/radar/settings`, next, { headers });
      setSettings(r.data);
      toast.success(
        r.data.enabled
          ? 'Ambient radar enabled — your opt-in signals are private to you.'
          : 'Ambient radar disabled.',
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  const accept = async () => {
    if (!acceptCluster) return;
    setBusyId(acceptCluster.cluster_id);
    try {
      const r = await axios.post(
        `${API}/cop/radar/clusters/${acceptCluster.cluster_id}/accept`,
        { notes: acceptNotes },
        { headers },
      );
      toast.success('CoP signal seeded from the radar · thanks for naming the pattern', {
        action: {
          label: 'View signal',
          onClick: () => { window.location.href = `/cop`; },
        },
        duration: 6000,
      });
      setClusters(prev => prev.filter(c => c.cluster_id !== acceptCluster.cluster_id));
      setAcceptCluster(null);
      setAcceptNotes('');
      // Soft signal to refresh the signal list
      window.dispatchEvent(new CustomEvent('cop:signal-created', { detail: { signal_id: r.data.signal_id } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Accept failed');
    } finally { setBusyId(null); }
  };

  const dismiss = async (cluster_id) => {
    setBusyId(cluster_id);
    try {
      await axios.post(`${API}/cop/radar/clusters/${cluster_id}/dismiss`, {}, { headers });
      setClusters(prev => prev.filter(c => c.cluster_id !== cluster_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dismiss failed');
    } finally { setBusyId(null); }
  };

  const hasPrompts = clusters.length > 0;
  // Always render at least a settings-access pill so users can discover + opt in.
  return (
    <>
      <Card
        className={`border ${hasPrompts ? 'border-[#A78BFA] bg-[#F5F3FF]' : 'border-[#E5E7EB] bg-white'}`}
        data-testid="radar-prompt-card"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className={`flex items-center gap-2 text-[14px] ${hasPrompts ? 'text-[#5B21B6]' : 'text-[#4B5563]'}`}>
              <Radar className={`w-4 h-4 ${hasPrompts ? 'text-[#7C3AED]' : 'text-[#9CA3AF]'}`} />
              Ambient Problem Resonance Radar
              {hasPrompts && (
                <Badge
                  variant="outline"
                  className="ml-1 border-[#A78BFA] text-[#5B21B6] bg-[#EDE9FE] text-[10px]"
                  data-testid="radar-count-badge"
                >
                  {clusters.length} new
                </Badge>
              )}
              {settings && !settings.enabled && (
                <Badge
                  variant="outline"
                  className="ml-1 border-[#D1D5DB] text-[#6B7280] bg-[#F9FAFB] text-[10px]"
                  data-testid="radar-disabled-badge"
                >
                  opt-in · off
                </Badge>
              )}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSettingsOpen(true)}
              data-testid="radar-settings-btn"
              className="text-[#6B7280]"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          <p className="text-[11px] text-[#6B7280] mt-1">
            {hasPrompts
              ? 'We noticed a pattern across your recent work. These prompts are private to you.'
              : 'When ≥3 members silently wrestle with similar problems, the radar quietly connects you. Default off — you opt in per source.'}
          </p>
        </CardHeader>

        {hasPrompts && (
          <CardContent className="space-y-3">
            {clusters.map(cl => (
              <div
                key={cl.cluster_id}
                className="bg-white border border-[#DDD6FE] rounded-lg p-3"
                data-testid={`radar-cluster-${cl.cluster_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0A0A0A]">
                      <Sparkles className="w-3.5 h-3.5 inline mr-1 text-[#7C3AED]" />
                      You and <span className="font-semibold">{Math.max(0, cl.members_count - 1)} other{cl.members_count - 1 === 1 ? '' : 's'}</span> have been
                      working on problems related to <span className="font-semibold text-[#5B21B6]">&ldquo;{cl.topic_label}&rdquo;</span> in the past 48h.
                    </p>
                    {cl.shared_tokens?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-1">
                        {cl.shared_tokens.map(tok => (
                          <span
                            key={tok}
                            className="text-[10px] bg-[#EDE9FE] text-[#5B21B6] px-1.5 py-0.5 rounded"
                          >
                            {tok}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Users className="w-4 h-4 text-[#9CA3AF] shrink-0" />
                </div>

                {cl.my_signals_preview?.length > 0 && (
                  <details className="text-[11px] text-[#6B7280] mt-2">
                    <summary className="cursor-pointer select-none hover:text-[#5B21B6]">
                      What we noticed from <em>your</em> signals ({cl.my_signals_preview.length})
                    </summary>
                    <ul className="mt-1 space-y-1 pl-3">
                      {cl.my_signals_preview.map((s, i) => (
                        <li key={i} className="flex gap-1.5">
                          <span className="text-[#A78BFA]">•</span>
                          <span className="italic">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className="flex gap-2 mt-2.5">
                  <Button
                    size="sm"
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white h-7 text-[11px]"
                    onClick={() => setAcceptCluster(cl)}
                    disabled={busyId === cl.cluster_id}
                    data-testid={`radar-accept-${cl.cluster_id}`}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Start a CoP signal
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px]"
                    onClick={() => dismiss(cl.cluster_id)}
                    disabled={busyId === cl.cluster_id}
                    data-testid={`radar-dismiss-${cl.cluster_id}`}
                  >
                    {busyId === cl.cluster_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
                    Not now
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      {/* Accept dialog */}
      <Dialog open={!!acceptCluster} onOpenChange={(o) => { if (!o) { setAcceptCluster(null); setAcceptNotes(''); } }}>
        <DialogContent className="max-w-md" data-testid="radar-accept-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radar className="w-4 h-4 text-[#7C3AED]" />
              Seed a CoP signal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              We&rsquo;ll open a new CoP signal with the topic <span className="font-semibold text-[#5B21B6]">&ldquo;{acceptCluster?.topic_label}&rdquo;</span> pre-seeded.
              Add a sentence or two describing the specific friction — other members who share this pattern will see it.
            </p>
            <Textarea
              value={acceptNotes}
              onChange={e => setAcceptNotes(e.target.value)}
              placeholder="What specifically have you been stuck on?"
              rows={3}
              maxLength={500}
              data-testid="radar-accept-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAcceptCluster(null); setAcceptNotes(''); }} data-testid="radar-accept-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={accept}
              disabled={busyId === acceptCluster?.cluster_id}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              data-testid="radar-accept-confirm-btn"
            >
              {busyId === acceptCluster?.cluster_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Seed signal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md" data-testid="radar-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radar className="w-4 h-4 text-[#7C3AED]" />
              Radar observability settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              The radar is <strong>strictly opt-in</strong>. Turn it on to let KStreak scan your own recent
              activity for shared struggle patterns. You control which signals feed it. We never expose
              other members&rsquo; names or content to you — only a count + topic.
            </p>

            <div className="border border-[#E5E7EB] rounded-lg p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={settings?.enabled || false}
                  disabled={saving}
                  onChange={(e) => saveSettings({ enabled: e.target.checked })}
                  data-testid="radar-enabled-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A0A0A]">Enable ambient radar</div>
                  <div className="text-[11px] text-[#6B7280]">
                    Scans every 15 min. You receive a private prompt only when ≥3 members share the pattern.
                  </div>
                </div>
              </label>

              {settings?.enabled && (
                <div className="pl-6 space-y-2 border-l-2 border-[#EDE9FE] pt-1">
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium">
                    Signal sources
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={settings?.sources?.chat_questions ?? true}
                      disabled={saving}
                      onChange={(e) => saveSettings({
                        sources: { ...(settings?.sources || {}), chat_questions: e.target.checked },
                      })}
                      data-testid="radar-source-chat-toggle"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-medium text-[#0A0A0A]">Unanswered chat questions</div>
                      <div className="text-[10px] text-[#6B7280]">
                        Your messages ending in <code>?</code> with no reply after 2h.
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={settings?.sources?.stuck_tasks ?? true}
                      disabled={saving}
                      onChange={(e) => saveSettings({
                        sources: { ...(settings?.sources || {}), stuck_tasks: e.target.checked },
                      })}
                      data-testid="radar-source-tasks-toggle"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-medium text-[#0A0A0A]">Stuck tasks</div>
                      <div className="text-[10px] text-[#6B7280]">
                        Tasks you own that have been in the same status for ≥72h.
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <p className="text-[10px] text-[#9CA3AF]">
              You can turn it off at any time. Already-detected clusters stop surfacing immediately.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setSettingsOpen(false)} data-testid="radar-settings-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
